/**
 * PD-16339 — media library vocabulary shared by crm-api, the CRM screens and
 * (through the address helpers) anything that renders a media item.
 *
 * Design: docs/claude/website-editor/08-site-builder-analysis.md §4.5.
 */

/** What a library item is. Video is not hosted by Workd (embeds link out). */
export const MEDIA_TYPES = ['image', 'svg', 'document'] as const;
export type MediaType = (typeof MEDIA_TYPES)[number];

/**
 * uploading: the row exists and the browser is sending the original.
 * processing: the original arrived; the background job is checking it and
 *   writing the public sizes.
 * ready: usable everywhere. failed: the job refused the file (failureReason).
 */
export const MEDIA_STATUSES = [
	'uploading',
	'processing',
	'ready',
	'failed',
] as const;
export type MediaStatus = (typeof MEDIA_STATUSES)[number];

/** Where a library item can be used; one row per use in media_usage. */
export const MEDIA_USAGE_TYPES = [
	'website',
	'email_template',
	'promo',
	'category',
	'brand',
	'storefront_layout',
] as const;
export type MediaUsageType = (typeof MEDIA_USAGE_TYPES)[number];

/**
 * The library's where-used filter: items used nowhere (`unused`), or items with at least
 * one use of that kind. Lets a person find what is safe to delete, or everything on the
 * websites, without opening items one by one.
 */
export const MEDIA_USED_FILTERS = ['unused', ...MEDIA_USAGE_TYPES] as const;
export type MediaUsedFilter = (typeof MEDIA_USED_FILTERS)[number];

/** A month as the library's month filter names it: `YYYY-MM`. */
export const MEDIA_MONTH_PATTERN = /^(\d{4})-(0[1-9]|1[0-2])$/;

/**
 * The span of a `YYYY-MM` month as the database compares it: from the first moment of that
 * month up to (not including) the first moment of the next. Null when `month` is not a
 * month. Dates are stored in UTC, so a month is a UTC month.
 */
export function mediaMonthRange(
	month: string,
): { start: string; end: string } | null {
	const match = MEDIA_MONTH_PATTERN.exec(month);
	if (match == null) {
		return null;
	}
	const year = Number(match[1]);
	const monthNumber = Number(match[2]);
	const nextYear = monthNumber === 12 ? year + 1 : year;
	const nextMonth = monthNumber === 12 ? 1 : monthNumber + 1;
	const pad = (value: number): string => String(value).padStart(2, '0');
	return {
		start: `${year}-${pad(monthNumber)}-01 00:00:00`,
		end: `${nextYear}-${pad(nextMonth)}-01 00:00:00`,
	};
}

/**
 * The public widths written for EVERY picture and SVG logo, whatever the size of
 * the original: a small original is enlarged to fill the wider ones. A website is
 * built once and served as fixed pages that list these addresses, and Replace keeps
 * an item's addresses; if the set of sizes followed the original, replacing a photo
 * with a smaller one would break every live page still listing the old wider sizes
 * (decided 2026-09-28). 2560 covers a full-width hero on a high-density screen.
 */
export const MEDIA_IMAGE_WIDTHS = [
	160, 320, 640, 960, 1280, 1920, 2560,
] as const;

/** Largest original the library accepts. */
export const MEDIA_MAX_UPLOAD_BYTES = 25 * 1024 * 1024;

/** Deleted items can be restored for this long, then they are purged. */
export const MEDIA_RESTORE_DAYS = 30;

/** One generated public size of a picture. */
export interface MediaSizeModel {
	width: number;
	height: number;
	bytes: number;
}

/** A public file other than the WebP sizes, e.g. the email-safe copy. */
export interface MediaRenditionModel {
	key: string;
	contentType: string;
	width: number;
	height: number;
	bytes: number;
}

export interface MediaRenditionsModel {
	/**
	 * For email: PNG when the picture has transparency, JPEG otherwise, at most
	 * 1200 px wide (a 600 px email column on a high-density screen). Email
	 * clients, Outlook above all, cannot show WebP or SVG.
	 */
	email?: MediaRenditionModel;
	/**
	 * The square PNG icon sizes written for this item (MEDIA_ICON_SIZES), at
	 * `icon-<size>.png`. Absent on items processed before icons existed, until the
	 * nightly media job adds them.
	 */
	icons?: Array<number>;
}

/**
 * Square PNG icons written for every picture and SVG logo, so any item can be a
 * website's icon (favicon): 32 for browser tabs, 180 for iPhone and iPad home
 * screens (Apple's apple-touch-icon), 192 for Android home screens. Browsers do
 * not reliably take WebP or SVG icons, which is why these are PNG. The picture is
 * fitted whole into the square (never cropped), on transparency — except 180,
 * which iOS would put on black, so it is on white.
 */
export const MEDIA_ICON_SIZES = [32, 180, 192] as const;
export type MediaIconSize = (typeof MEDIA_ICON_SIZES)[number];

/** File name of an item's square icon of this size, under `media/<publicId>/`. */
export function mediaIconFileName(size: number): string {
	return `icon-${size}.png`;
}

/** Widest email-safe copy; emails are laid out at 600 px. */
export const MEDIA_EMAIL_MAX_WIDTH = 1200;

/**
 * The key of a public file under the media base. Addresses carry the item's
 * public id and never the tenant: the id is unguessable and ownership lives in
 * the tenant's own database. Fixed names (rather than the uploaded file name)
 * are what let Replace keep the address.
 */
export function mediaObjectKey(
	publicId: string,
	type: MediaType,
	width?: number,
): string {
	if (type === 'svg') {
		return `media/${publicId}/image.svg`;
	}
	if (type === 'document') {
		return `media/${publicId}/document.pdf`;
	}
	// Pictures: one WebP per width.
	return `media/${publicId}/${width ?? MEDIA_IMAGE_WIDTHS[0]}.webp`;
}

/** Full public address of one file, from a media base such as `https://cdn.titanworkflow.com`. */
export function mediaUrl(
	mediaBase: string,
	publicId: string,
	type: MediaType,
	width?: number,
): string {
	return `${mediaBase.replace(/\/+$/, '')}/${mediaObjectKey(publicId, type, width)}`;
}

/**
 * Key of any one public file of an item by its file name (`640.webp`, `email.png`,
 * `image.svg`, `document.pdf`). Every public file of an item sits under
 * `media/<publicId>/`, which is what lets a purge remove them all by prefix.
 */
export function mediaFileKey(publicId: string, fileName: string): string {
	return `media/${publicId}/${fileName}`;
}

/** Key of the email-safe copy of a picture (see MediaRenditionsModel.email). */
export function mediaEmailObjectKey(
	publicId: string,
	format: 'png' | 'jpg',
): string {
	return `media/${publicId}/email.${format}`;
}
